import React, { useEffect, useState } from "react";

import "./Register.scss";
import BackgroundVisuals from "@components/BackgroundVisuals/BackgroundVisuals";
import { phoneRegExp, windowLocationOrigin } from "@utils/validation";
import ButtonLoader from "@components/ButtonLoader/ButtonLoader";
import { observer } from "mobx-react-lite";
import { useMst } from "@store/Route";
import { useFormik } from "formik";
import * as Yup from "yup";
import { RouteComponentProps, useHistory } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import TextInput from "@components/textInput/TextInput";
import Swal from "sweetalert2";
import { isEmail, numberOnly } from "@utils/helper";
import { AUTH_LOGIN, RECAPTCHA_KEY } from "@constants/api";

const Register = observer((props: RouteComponentProps) => {
  const [showPasswordInputAsText, setShowPasswordInputAsText] = useState(false);
  const { register } = useMst();
  const history = useHistory();

  const { checkExistingEmail } = register;

  // temp for checking if email is already registered
  const [isEmailExisting, setEmailsExisting] = useState(false);

  const formik = useFormik({
    initialValues: {
      salutation: "mrs",
      firstName: "",
      lastName: "",
      email: "",
      mobileNo: "",
      password: "",
      retypePassword: "",
      billingName: "",
      blockNo: "",
      address: "",
      buildingName: "",
      postalCode: "",
      unitNo: "",
      isTermAndConditions: false,
      isWaiverAndIndemnity: false,
      isPrivacyPolicy: false,
      billingEmails: [""],
      token: "",
      fullAddress: "",
    },
    validationSchema: Yup.object({
      salutation: Yup.string().required("Salutation is required"),
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      mobileNo: Yup.string()
        .matches(phoneRegExp, "Phone number is not valid")
        .required("Phone number is required"),
      password: Yup.string()
        .required("Password is required")
        .min(6, "Password must be at least 6 characters"),
      retypePassword: Yup.string().oneOf(
        [Yup.ref("password"), null],
        "Passwords must match"
      ),
      billingName: Yup.string().required("Billing Name is required"),
      blockNo: Yup.string().required("Block No is required"),
      address: Yup.string().required("Address is required"),
      buildingName: Yup.string().required("Building Name is required"),
      postalCode: Yup.string().required("Postal Code is required"),
      isTermAndConditions: Yup.bool()
        .equals([true], "You need to agree to our Term And Conditions.")
        .required("You need to agree to our Term And Conditions."),
      isWaiverAndIndemnity: Yup.bool()
        .equals(
          [true],
          "You need to agree to our Waiver and Indemnity to proceed."
        )
        .required("You need to agree to our Waiver and Indemnity to proceed."),
      isPrivacyPolicy: Yup.bool().equals(
        [true],
        "You need to agree to our Privacy Policy."
      ),
      billingEmails: Yup.array()
        .ensure()
        .of(Yup.string().email("Invalid email address")),
      token: Yup.string().required("You need to check reCAPTCHA"),
      unitNo: Yup.string().nullable().notRequired(),
    }),
    onSubmit: (values) => {
      register.postRegister(values, (result: any) => {
        Swal.fire({
          icon: "success",
          title: "Register has been success",
          timer: 1500,
        }).then(() => {
          history.push("/");
        });
      });
    },
  });

  useEffect(() => {
    register.resetRegister();
  }, []);

  useEffect(() => {
    const email = formik.values.email;
    let timerFunc = setTimeout(() => {
      if (isEmail(email)) {
        checkExistingEmail(email, (callback) => {
          console.log(callback);
          if (callback === "email_not_exists") {
            setEmailsExisting(false);
            return;
          }
          setEmailsExisting(true);
        });
      }
    }, 1000);
    return () => clearTimeout(timerFunc);
  }, [formik.values.email]);

  return (
    <form
      onSubmit={(e) => {
        formik.handleSubmit();
        e.preventDefault();
      }}
      className="Register"
    >
      <div className="registerForms__overallWrapper">
        <h1 className="header--30 color--white Register__headerMain">
          Create account
        </h1>

        <h1 className="header--18 color--white registerForms__headerSub">
          Personal Details
        </h1>

        <div className="Register__form">
          <div className="row no-gutters">
            <div className="col-6 pl-3 pr-3">
              <div className="formElements__group">
                <label className="formElements__label">Salutation</label>
                <select
                  {...formik.getFieldProps("salutation")}
                  className={`w-100 formElements__dropdown 
								${
                  formik.touched.salutation && formik.errors.salutation
                    ? "formElements__errorBorder"
                    : ""
                }`}
                  required
                >
                  <option value="placeholder" disabled>
                    Select your option
                  </option>
                  <option value="mrs">Mrs</option>
                  <option value="miss">Miss</option>
                  <option value="mr">Mr</option>
                  <option value="mdm">Mdm</option>
                </select>
              </div>
            </div>
          </div>

          <div className="row no-gutters">
            <div className="col-md-6">
              <TextInput
                title="First Name *"
                placeholder="Input First Name"
                {...formik.getFieldProps("firstName")}
                touched={formik.touched.firstName}
                error={formik.errors.firstName}
              ></TextInput>
            </div>
            <div className="col-md-6">
              <TextInput
                title="Last Name *"
                placeholder="Input Last Name"
                {...formik.getFieldProps("lastName")}
                touched={formik.touched.lastName}
                error={formik.errors.lastName}
              ></TextInput>
            </div>
          </div>

          <div className="row no-gutters">
            <div className="col-md-6">
              <TextInput
                title="Email (This will be your log in id)*"
                placeholder="Input Email"
                {...formik.getFieldProps("email")}
                touched={formik.touched.email}
                error={formik.errors.email}
                required
              ></TextInput>
              {isEmailExisting ? (
                <p className="mb-3 text-red-500">
                  Email is registered, please login. If you do not know the
                  password, please click on Forget Password.
                </p>
              ) : null}
            </div>
            <div className="col-md-6">
              <TextInput
                title="Mobile No.*"
                placeholder="Input Mobile No"
                {...formik.getFieldProps("mobileNo")}
                touched={formik.touched.mobileNo}
                error={formik.errors.mobileNo}
                required
              ></TextInput>
            </div>
          </div>
          <div className="row no-gutters">
            <div className="col-md-6">
              <div className="formElements__group">
                <label className="formElements__label">Password*</label>

                <div className="formElements__inputWrapper_w_floater">
                  <input
                    className={`w-100 form-control formElements__input formElements--inputText
									${
                    formik.touched.password && formik.errors.password
                      ? "formElements__errorBorder"
                      : ""
                  }
									`}
                    type={showPasswordInputAsText ? "text" : "password"}
                    placeholder="Input Password"
                    required
                    aria-errormessage="Password needs to be at least 6 characters long."
                    {...formik.getFieldProps("password")}
                  />
                  {formik.touched.password && formik.errors.password ? (
                    <div className="formElements__errorText">
                      {formik.errors.password}
                    </div>
                  ) : null}
                  <div className="invalid-feedback" />
                  <div
                    className="formElements__floaterElement is--text"
                    onClick={() => {
                      setShowPasswordInputAsText(!showPasswordInputAsText);
                    }}
                  >
                    {showPasswordInputAsText ? "hide" : "show"}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="formElements__group">
                <label className="formElements__label">Retype Password*</label>

                <div className="formElements__inputWrapper_w_floater">
                  <input
                    className={`w-100 form-control formElements__input formElements--inputText
									${
                    formik.touched.retypePassword &&
                    formik.errors.retypePassword
                      ? "formElements__errorBorder"
                      : ""
                  }
									`}
                    type={showPasswordInputAsText ? "text" : "password"}
                    required
                    placeholder="Input Retype Password"
                    aria-errormessage="Password needs to be at least 6 characters long."
                    {...formik.getFieldProps("retypePassword")}
                  />
                  {formik.touched.retypePassword &&
                  formik.errors.retypePassword ? (
                    <div className="formElements__errorText">
                      {formik.errors.retypePassword}
                    </div>
                  ) : null}
                  <div className="invalid-feedback" />
                  <div
                    className="formElements__floaterElement is--text"
                    onClick={() => {
                      setShowPasswordInputAsText(!showPasswordInputAsText);
                    }}
                  >
                    {showPasswordInputAsText ? "hide" : "show"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row no-gutters pt-5">
            <h1 className="pl-3 pr-3 formElements__inputSectionHeader header--18">
              Billing Info
            </h1>
          </div>
          <div className="row no-gutters">
            <div className="col-md-6">
              <TextInput
                title="Billing Name*"
                placeholder="Input Billing Name"
                {...formik.getFieldProps("billingName")}
                touched={formik.touched.billingName}
                error={formik.errors.billingName}
                required
              ></TextInput>
            </div>
          </div>

          <div className="row no-gutters">
            <div className="col-md-6">
              <TextInput
                title="Postal Code*"
                placeholder="Input Postal Code"
                {...formik.getFieldProps("postalCode")}
                onChange={(event) => {
                  formik.setFieldValue(
                    "postalCode",
                    numberOnly(event.target.value)
                  );
                }}
              ></TextInput>
            </div>
          </div>

          <div className="row no-gutters">
            <div className="col-md-6">
              <TextInput
                title="Block No.*"
                placeholder="Input Block No"
                {...formik.getFieldProps("blockNo")}
                touched={formik.touched.blockNo}
                error={formik.errors.blockNo}
                required
              ></TextInput>
            </div>
          </div>

          <div className="row no-gutters">
            <div className="col-md-6">
              <TextInput
                title="Address*"
                placeholder="Input Address"
                {...formik.getFieldProps("address")}
                touched={formik.touched.address}
                error={formik.errors.address}
                required
              ></TextInput>
            </div>
          </div>

          <div className="row no-gutters">
            <div className="col-md-3">
              <TextInput
                title="Unit No. (Optional)"
                placeholder="Input Unit No"
                {...formik.getFieldProps("unitNo")}
                touched={formik.touched.unitNo}
                error={formik.errors.unitNo}
              ></TextInput>
            </div>
            <div className="col-md-3">
              <TextInput
                title="Building Name *"
                placeholder="Input Building Name"
                {...formik.getFieldProps("buildingName")}
                touched={formik.touched.buildingName}
                error={formik.errors.buildingName}
              ></TextInput>
            </div>
          </div>

          <div className="row no-gutters pt-5">
            <h1 className="pl-3 pr-3 formElements__inputSectionHeader header--18">
              Billing Emails
            </h1>
          </div>
          <div className="row no-gutters">
            <div className="formElements__group">
              <label className="formElements__label">Billing emails *</label>
            </div>
            {formik.values.billingEmails.map((_, number) => {
              let billingEmail = formik.values.billingEmails[number];
              let billingEmailsTemp = [...formik.values.billingEmails];
              return (
                <div className="row col-md-6 no-gutters" key={number}>
                  <div className="col-11">
                    <div className="formElements__group">
                      <input
                        className={`w-100 form-control formElements__input formElements--inputText
											${
                        formik.touched.billingEmails &&
                        formik.errors.billingEmails &&
                        formik.errors.billingEmails[number]
                          ? "formElements__errorBorder"
                          : ""
                      }
											`}
                        placeholder="Input Email"
                        required
                        type="email"
                        value={billingEmail}
                        onChange={(event) => {
                          billingEmailsTemp[number] = event.target.value;

                          formik.setValues({
                            ...formik.values,
                            billingEmails: billingEmailsTemp,
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div
                    className="col-1 pl-3 pr-3 formElements__floaterElement is--delete"
                    onClick={() => {
                      billingEmailsTemp.splice(number, 1);

                      formik.setValues({
                        ...formik.values,
                        billingEmails: billingEmailsTemp,
                      });
                    }}
                  ></div>
                </div>
              );
            })}
          </div>
          <div
            className="row center add"
            onClick={() => {
              const billingEmailsTemp = formik.values.billingEmails;
              billingEmailsTemp.push("");

              formik.setValues({
                ...formik.values,
                billingEmails: billingEmailsTemp,
              });
            }}
          >
            + Add Billing email
          </div>

          <div className="js-multiInputWrapper w-100 formElements__group pl-3 pr-3 pb-5 mt-3">
            <div className="row no-gutters">
              <label className="col-12 formElements__checkbox_itemWrapper">
                <input
                  type="checkbox"
                  className="formElements__checkbox_defaultCheckbox"
                  {...formik.getFieldProps("isTermAndConditions")}
                />
                <div className="formElements__checkbox_customCheckbox" />
                <div className="formElements__checkbox_label">
                  I agree to the{" "}
                  <span
                    onClick={() =>
                      window.open(
                        `${windowLocationOrigin()}/term-and-conditions`,
                        "_blank"
                      )
                    }
                    style={{
                      textDecoration: "underline",
                    }}
                  >
                    Term & Conditions.
                  </span>
                </div>
              </label>
              {formik.errors.isTermAndConditions &&
              formik.touched.isTermAndConditions ? (
                <div className="formElements__errorText">
                  {formik.errors.isTermAndConditions}
                </div>
              ) : null}
              <label className="col-12 formElements__checkbox_itemWrapper">
                <input
                  type="checkbox"
                  className="formElements__checkbox_defaultCheckbox"
                  {...formik.getFieldProps("isWaiverAndIndemnity")}
                />
                <div className="formElements__checkbox_customCheckbox" />
                <div className="formElements__checkbox_label">
                  I agree to the{" "}
                  <span
                    onClick={() =>
                      window.open(
                        `${windowLocationOrigin()}/waiver-and-indemnity`,
                        "_blank"
                      )
                    }
                    style={{
                      textDecoration: "underline",
                    }}
                  >
                    Waiver and Indemnity.
                  </span>
                </div>
              </label>
              {formik.errors.isWaiverAndIndemnity &&
              formik.touched.isWaiverAndIndemnity ? (
                <div className="formElements__errorText">
                  {formik.errors.isWaiverAndIndemnity}
                </div>
              ) : null}
              <label className="col-12 formElements__checkbox_itemWrapper">
                <input
                  type="checkbox"
                  className="formElements__checkbox_defaultCheckbox"
                  {...formik.getFieldProps("isPrivacyPolicy")}
                />
                <div className="formElements__checkbox_customCheckbox" />
                <div className="formElements__checkbox_label">
                  I agree to the{" "}
                  <span
                    onClick={() =>
                      window.open(`${windowLocationOrigin()}/PDPA`, "_blank")
                    }
                    style={{
                      textDecoration: "underline",
                      verticalAlign: "middle",
                      paddingLeft: "5px",
                    }}
                  >
                    PDPA.
                  </span>
                </div>
              </label>
              {formik.errors.isPrivacyPolicy &&
              formik.touched.isPrivacyPolicy ? (
                <div className="formElements__errorText">
                  {formik.errors.isPrivacyPolicy}
                </div>
              ) : null}
            </div>
            <div className="fixedDropdownErrorMsg">
              Please select an option.
            </div>
          </div>

          <ReCAPTCHA
            sitekey={RECAPTCHA_KEY}
            size="compact"
            onChange={(token) => {
              console.log(token);
              formik.setValues({
                ...formik.values,
                token: token || "",
              });
            }}
          />

          {formik.errors.token && formik.touched.token ? (
            <div className="formElements__errorText">{formik.errors.token}</div>
          ) : null}

          <div className="row no-gutters pt-5">
            <div className="offset-md-6 col-md-3 pr-3">
              <button
                onClick={() => {
                  props.history.replace("/");
                }}
                type="button"
                className="w-100 mt-3 formElements__buttonCancel"
              >
                Close
              </button>
            </div>
            <div className="col-md-3">
              <ButtonLoader
                type="submit"
                className="w-100 mt-3"
                isLoading={register.state === "pending"}
                isDisabled={isEmailExisting}
              >
                Register
              </ButtonLoader>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
});

export default Register;
